import React, { useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from 'components/common/Button';
import CloseIcon from '@material-ui/icons/Close';
import Form from 'components/form';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import DateInput from 'components/form/DateInput';
import { useQuery } from 'react-apollo';
import { useMutation } from '@apollo/react-hooks';
import useSnack from 'hooks/useSnack';
import {
  GET_ESSAY_PROMPTS,
  GET_STATEMENTS,
  ADD_STATEMENT,
  UPDATE_STATEMENT,
  GET_ESSAY_TOPICS,
} from 'graphql/essays';
import { GET_SCHOOLS } from 'graphql/student_college';
import { PROMPTS_CATEGORIES } from 'constant/essay';

const FormItem = Form.FormItem;

const useStyles = makeStyles((theme) => ({
  label: {
    display: 'block',
    position: 'initial',
    transform: 'scale(.9)',
    fontWeight: 400,
    color: '#626C82',
    marginBottom: theme.spacing(1),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const AddStatementModal = ({ onClose, form, statement, student }) => {
  const classes = useStyles();
  const snack = useSnack();
  const { data, loading } = useQuery(GET_ESSAY_PROMPTS, {
    variables: { userId: student.id },
  });
  const { data: topics } = useQuery(GET_ESSAY_TOPICS, {
    variables: { userId: student.id },
  });
  const { data: schools } = useQuery(GET_SCHOOLS, {
    variables: {
      student_sub: student.sub,
    },
  });
  const [addStatement] = useMutation(ADD_STATEMENT);
  const [updateStatement] = useMutation(UPDATE_STATEMENT);
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then((values) => {
      if (statement) {
        return updateStatement({
          variables: {
            id: statement.id,
            data: {
              ...values,
              due_date: moment(values.due_date).format('YYYY-MM-DD'),
            },
          },
        }).then(
          () => {
            snack.success('Personal Statement updated');
            onClose();
          },
          (e) => snack.error(e.message),
        );
      }

      return addStatement({
        variables: {
          data: {
            ...values,
            due_date: moment(values.due_date).format('YYYY-MM-DD'),
            user_id: student.id,
          },
        },
        update: (proxy, mutationResult) => {
          const data = proxy.readQuery({
            query: GET_STATEMENTS,
            variables: {
              userId: student.id,
            },
          });
          if (data) {
            data.rows.push(mutationResult.data.row);
          }
          proxy.writeQuery({
            query: GET_STATEMENTS,
            variables: {
              userId: student.id,
            },
            data: {
              rows: data ? data.rows : [mutationResult.data.row],
            },
          });
        },
      }).then(
        () => {
          snack.success('Personal Statement saved');
          onClose();
        },
        (e) => snack.error(e.message),
      );
    });
  };

  return (
    <Dialog open fullWidth maxWidth={'sm'} onClose={onClose}>
      <MuiDialogTitle>
        <Typography gutterBottom variant="subtitle1">
          {statement ? 'Update Personal Statement' : 'Add Personal Statement'}
        </Typography>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <DialogContent>
        <Form>
          <FormItem>
            {getFieldDecorator('name', {
              initialValue: statement?.name,
              rules: [{ required: true, message: 'Required' }],
            })(<Input label="Personal Statement Name" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('due_date', {
              initialValue: statement?.due_date,
              rules: [{ required: true, message: 'Required' }],
            })(
              <DateInput
                fullWidth
                label="Due date"
                inputVariant="outlined"
                format="MMM DD, YYYY"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('topic_id', {
              initialValue: statement?.topic_id,
            })(
              <Select
                transformValue
                isMulti={false}
                options={topics?.essay_topics.map((i) => ({
                  label: i.idea,
                  value: i.id,
                }))}
                label="Select Topic"
              />,
            )}
          </FormItem>

          <FormItem>
            {getFieldDecorator('prompt_id', {
              initialValue: statement?.prompt_id,
            })(
              <Select
                transformValue
                isMulti={false}
                loading={loading}
                options={PROMPTS_CATEGORIES.map((i) => ({
                  label: i.text,
                  value: i.id,
                }))}
                label="Select Prompt"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('schools', {
              initialValue: statement?.schools,
            })(
              <Select
                options={schools?.student_college.map((i) => ({
                  label: i.college_name,
                  value: i.college_id,
                }))}
                placeholder="Select"
                label="Schools"
              />,
            )}
          </FormItem>
        </Form>
        <Box textAlign="center" marginTop={1} paddingBottom={2}>
          <Button
            size="large"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
          >
            {statement ? 'Update' : 'Add'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Form.create()(AddStatementModal);
